import { Link } from "react-router-dom";
import type { Movie } from "@/lib/movies";
import { categories, formatDuration } from "@/lib/movies";

interface MovieCardProps {
  movie: Movie;
  compact?: boolean;
}

export default function MovieCard({ movie, compact = false }: MovieCardProps) {
  const category = categories.find((item) => item.slug === movie.type);

  return (
    <article className="group">
      <Link
        to={`/detail/${movie.id}`}
        className="block rounded-lg overflow-hidden bg-background-100 border border-background-200/80 hover:border-primary-400/70 transition-colors duration-300 cursor-pointer"
      >
        <div className="relative w-full aspect-[2/3] overflow-hidden bg-background-200">
          <img
            src={movie.poster}
            alt={`${movie.title}（${movie.year}）韩国伦理电影海报`}
            title={`${movie.title} - 韩国伦理电影在线观看`}
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/10 to-transparent opacity-90"></div>

          <span className="absolute top-2.5 right-2.5 h-7 min-w-[46px] px-1.5 rounded-md bg-accent-500 text-background-50 font-label text-sm font-semibold flex items-center justify-center">
            {movie.rating.toFixed(1)}
          </span>

          <span className="absolute top-2.5 left-2.5 h-6 px-2 rounded bg-background-50/80 text-foreground-900 text-[11px] flex items-center justify-center backdrop-blur-sm">
            {movie.ageRating}
          </span>

          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span className="w-12 h-12 rounded-full bg-primary-500 text-background-50 flex items-center justify-center">
              <i className="ri-play-fill text-2xl" aria-hidden="true"></i>
            </span>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-3">
            <span className="inline-flex items-center h-6 px-2 rounded bg-primary-500/90 text-background-50 text-[11px] font-medium">
              {category?.name ?? "伦理电影"}
            </span>
          </div>
        </div>

        <div className={compact ? "p-2.5" : "p-3.5"}>
          <h3 className="font-heading text-foreground-950 font-medium text-sm md:text-[15px] leading-snug line-clamp-1 group-hover:text-primary-400 transition-colors">
            {movie.title}
          </h3>
          <p className="mt-1 text-[11px] text-foreground-500 font-label tracking-wide line-clamp-1">
            {movie.originalTitle}
          </p>
          <div className="mt-2 flex items-center gap-2 text-[11px] text-foreground-600">
            <span>{movie.year}</span>
            <span className="w-1 h-1 rounded-full bg-background-400"></span>
            <span className="truncate">{formatDuration(movie.duration)}</span>
          </div>
          {!compact && (
            <div className="mt-2.5 flex flex-wrap gap-1">
              {movie.tags.slice(0, 2).map((tag) => (
                <span
                  key={tag}
                  className="h-5 px-1.5 rounded bg-secondary-100/60 text-secondary-900 text-[10px] flex items-center"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </Link>
    </article>
  );
}